import { useEffect, useState } from 'react';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { CurrencyIcon } from '~/components/collections';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks/useWalletIcon';
import { rage, useObservable } from '~/rage';
import { Currency } from '~/shared';
import parking from '../../assets/images/shop_player/parking.png';
import passport from '../../assets/images/shop_player/passport.png';
import {
	setModalChangeNamePendingVisible,
	setModalChangeNameRejectedVisible,
	setModalChangeNameVisible,
	useLayoutDispatch,
} from '../../redux';
import { formatTime } from '../../utils';
import { ShopBanner } from '../ShopBanner';
import {
	StyledBottomContainer,
	StyledContainer,
	StyledDiscountTimerContainer,
	StyledItemContainer,
	StyledItemImage,
	StyledItemsContainer,
	StyledMoreBlock,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledTagsContainer,
} from './ShopPlayer.styled';

type PriceConfig = {
	price: number;
	discount: number | null;
	discountTimestampStart: number | null;
	discountTimestampEnd: number | null;
};

const hasActiveDiscount = (priceConfig: PriceConfig): boolean => {
	if (!priceConfig.discount) {
		return false;
	}

	if (
		priceConfig.discountTimestampStart === null ||
		priceConfig.discountTimestampEnd === null
	) {
		return false;
	}

	const now = Date.now();
	return (
		now >= priceConfig.discountTimestampStart &&
		now <= priceConfig.discountTimestampEnd
	);
};

const getDiscountPrice = (priceConfig: PriceConfig): number => {
	if (!hasActiveDiscount(priceConfig)) {
		return priceConfig.price;
	}

	return Math.floor(priceConfig.price * (1 - priceConfig.discount!));
};

const getDiscountTime = (priceConfig: PriceConfig): string => {
	if (!hasActiveDiscount(priceConfig) || !priceConfig.discountTimestampEnd) {
		return '';
	}

	const { time, unit } = formatTime(priceConfig.discountTimestampEnd);
	return unit ? `${time} ${unit}` : time;
};

const ShopPlayer = () => {
	const dispatch = useLayoutDispatch();
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const parkingSlotPriceConfig = useObservable(
		rage.player.getParkingSlotPriceConfig(),
	);
	const changeNamePriceConfig = useObservable(
		rage.player.getChangeNamePriceConfig(),
	);
	const changeNameApplication = useObservable(
		rage.donate.getChangeNameApplication(),
	);

	const [parkingSlotDiscountTime, setParkingSlotDiscountTime] = useState('');
	const [changeNameDiscountTime, setChangeNameDiscountTime] = useState('');

	useEffect(() => {
		const updateDiscountTimers = () => {
			setParkingSlotDiscountTime(getDiscountTime(parkingSlotPriceConfig));
			setChangeNameDiscountTime(getDiscountTime(changeNamePriceConfig));
		};

		updateDiscountTimers();
		const timer = setInterval(updateDiscountTimers, 1000);

		return () => clearInterval(timer);
	}, [parkingSlotPriceConfig, changeNamePriceConfig]);

	const hasParkingSlotDiscount = hasActiveDiscount(parkingSlotPriceConfig);
	const hasChangeNameDiscount = hasActiveDiscount(changeNamePriceConfig);
	const parkingSlotPrice = getDiscountPrice(parkingSlotPriceConfig);
	const changeNamePrice = getDiscountPrice(changeNamePriceConfig);

	return (
		<StyledContainer>
			<ShopBanner />
			<StyledItemsContainer>
				<StyledItemContainer
					onClick={() => rage.player.showBuyParkingSlotsModal()}
				>
					<StyledShadow />
					<StyledTagsContainer>
						{hasParkingSlotDiscount && parkingSlotDiscountTime && (
							<StyledDiscountTimerContainer>
								<Text>{parkingSlotPriceConfig.discount! * 100}%</Text>
								<div className={'divider'} />
								<Text>{parkingSlotDiscountTime}</Text>
							</StyledDiscountTimerContainer>
						)}
					</StyledTagsContainer>
					<StyledItemImage src={parking} />
					<StyledBottomContainer>
						<Text>Гаражное место</Text>
						<StyledPriceContainer>
							<StyledPrice $isDonate>
								<CurrencyIcon icon={donateIcon} size="md" />
								<Text>{parkingSlotPrice.toLocaleString('ru-RU')}</Text>
							</StyledPrice>
							{hasParkingSlotDiscount && (
								<StyledOldPrice>
									<CurrencyIcon icon={donateIcon} size={10} />
									<Text>
										{parkingSlotPriceConfig.price.toLocaleString('ru-RU')}
									</Text>
									<div className={'line'} />
								</StyledOldPrice>
							)}
						</StyledPriceContainer>
						<StyledMoreBlock>
							<Text>Подробнее</Text>
							<img src={arrowRight} />
						</StyledMoreBlock>
					</StyledBottomContainer>
				</StyledItemContainer>
				<StyledItemContainer
					onClick={() => {
						if (changeNameApplication?.status === 'pending') {
							dispatch(setModalChangeNamePendingVisible(true));
						} else if (changeNameApplication?.status === 'rejected') {
							dispatch(setModalChangeNameRejectedVisible(true));
						} else {
							dispatch(setModalChangeNameVisible(true));
						}
					}}
				>
					<StyledShadow />
					<StyledTagsContainer>
						{hasChangeNameDiscount && changeNameDiscountTime && (
							<StyledDiscountTimerContainer>
								<Text>{changeNamePriceConfig.discount! * 100}%</Text>
								<div className={'divider'} />
								<Text>{changeNameDiscountTime}</Text>
							</StyledDiscountTimerContainer>
						)}
					</StyledTagsContainer>
					<StyledItemImage src={passport} />
					<StyledBottomContainer>
						<Text>Смена имени</Text>
						<StyledPriceContainer>
							<StyledPrice $isDonate>
								<CurrencyIcon icon={donateIcon} size="md" />
								<Text>{changeNamePrice.toLocaleString('ru-RU')}</Text>
							</StyledPrice>
							{hasChangeNameDiscount && (
								<StyledOldPrice>
									<CurrencyIcon icon={donateIcon} size={10} />
									<Text>
										{changeNamePriceConfig.price.toLocaleString('ru-RU')}
									</Text>
									<div className={'line'} />
								</StyledOldPrice>
							)}
						</StyledPriceContainer>
						<StyledMoreBlock>
							<Text>Подробнее</Text>
							<img src={arrowRight} />
						</StyledMoreBlock>
					</StyledBottomContainer>
				</StyledItemContainer>
			</StyledItemsContainer>
		</StyledContainer>
	);
};

export { ShopPlayer };
